import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Layout } from "../components/Layout";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { CustomerTabs } from "../components/CustomerTabs";
import { CustomerLogo } from "../components/CustomerLogo";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { num, fmtDate } from "../lib/format";

const money = (v) => (v == null || isNaN(v) ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const tie = (a, b) => (a != null && b != null && Math.abs(Number(a) - Number(b)) < 0.01);

export function CustomerHeader({ clientId, c, active }) {
  return (
    <>
      <Breadcrumbs items={[{ label: "Portfolio", to: "/" }, { label: c.client_name }]} />
      <div className="mt-4 flex items-center gap-4 border-b border-border pb-5">
        <CustomerLogo clientId={clientId} hasLogo={c.has_logo} size={56} iconSize={26} />
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black">{c.client_name}</h1>
          <p className="mt-1 font-mono text-sm uppercase tracking-wide text-muted-foreground">
            {c.client_short_name}{c.provider_company_code ? ` · Co ${c.provider_company_code}` : ""}
          </p>
        </div>
      </div>
      <CustomerTabs clientId={clientId} active={active} />
    </>
  );
}

export function RunSelect({ runs, value, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="font-semibold text-muted-foreground">Pay Register</span>
      <select data-testid="run-select" value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-md border border-border bg-background px-2 text-sm font-semibold">
        {runs.map((r) => <option key={r.payroll_run_GUID} value={r.payroll_run_GUID}>{r.run_label} — {fmtDate(r.pay_date)}</option>)}
      </select>
    </label>
  );
}

export default function PayrollDashboard() {
  const { clientId } = useParams();
  const qc = useQueryClient();
  const [runId, setRunId] = useState(null);
  const [recon, setRecon] = useState(null);
  const [commentary, setCommentary] = useState("");

  const { data: client, isLoading: cl } = useQuery({
    queryKey: ["client", clientId], queryFn: async () => (await api.get(`/clients/${clientId}`)).data,
  });
  const runs = client?.runs || [];
  const activeRun = runId || runs[0]?.payroll_run_GUID;

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", activeRun],
    queryFn: async () => (await api.get(`/runs/${activeRun}/dashboard`)).data,
    enabled: !!activeRun,
  });

  useEffect(() => {
    if (!data) return;
    setRecon(Object.fromEntries(data.recon.map((r) => [r.key, { liability: r.liability ?? "", register: r.register ?? "" }])));
    setCommentary(data.commentary || "");
  }, [data?.run?.payroll_run_GUID]); // hydrate once per run; don't clobber in-progress edits on refetch

  const save = useMutation({
    mutationFn: () => api.put(`/runs/${activeRun}/dashboard/recon`, { recon, commentary }),
    onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["dashboard", activeRun] }); },
    onError: () => toast.error("Save failed"),
  });

  if (cl) return <Layout><Skeleton className="h-8 w-72" /><Skeleton className="mt-6 h-96 rounded-md" /></Layout>;
  const c = client?.client || {};
  const run = data?.run;

  return (
    <Layout>
      <CustomerHeader clientId={clientId} c={c} active="dashboard" />
      {runs.length === 0 ? (
        <div className="mt-8 rounded-md border border-border bg-card p-8 text-center text-muted-foreground">No payroll runs yet.</div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <RunSelect runs={runs} value={activeRun} onChange={setRunId} />
            {run && (
              <div className="flex flex-wrap gap-4 font-mono text-xs text-muted-foreground">
                <span>Check Date <b className="text-foreground">{fmtDate(run.pay_date)}</b></span>
                <span>Period <b className="text-foreground">{fmtDate(run.period_start_date)} – {fmtDate(run.period_end_date)}</b></span>
              </div>
            )}
          </div>

          {isLoading ? <Skeleton className="mt-6 h-96 rounded-md" /> : (
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              {/* Payroll summary */}
              <div className="rounded-lg border border-border bg-card p-4" data-testid="payroll-summary">
                <h2 className="mb-3 font-heading text-base font-bold uppercase tracking-wide">Payroll Summary</h2>
                <table className="w-full text-sm">
                  <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left font-semibold pb-2"></th><th className="text-right font-semibold pb-2">Current</th>
                    <th className="text-right font-semibold pb-2">Previous</th><th className="text-right font-semibold pb-2">Difference</th>
                  </tr></thead>
                  <tbody>
                    {data.summary.map((s) => (
                      <tr key={s.label} className="border-t border-border" data-testid={`summary-${s.label}`}>
                        <td className="py-2 font-medium">{s.label}</td>
                        <td className="py-2 text-right font-mono">{s.money ? money(s.current) : num(s.current)}</td>
                        <td className="py-2 text-right font-mono text-muted-foreground">{s.money ? money(s.previous) : num(s.previous)}</td>
                        <td className={`py-2 text-right font-mono ${s.difference > 0 ? "text-pos" : s.difference < 0 ? "text-neg" : ""}`}>
                          {s.money ? money(s.difference) : num(s.difference)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Bank reconciliation (manual entry) */}
              <div className="rounded-lg border border-border bg-card p-4" data-testid="bank-recon">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="font-heading text-base font-bold uppercase tracking-wide">Bank Reconciliation</h2>
                  <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">Manual entry</span>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left font-semibold pb-2"></th><th className="text-right font-semibold pb-2">Liability Rpt</th>
                    <th className="text-right font-semibold pb-2">Register</th><th className="text-right font-semibold pb-2">Tie</th>
                  </tr></thead>
                  <tbody>
                    {data.recon.map((r) => {
                      const v = recon?.[r.key] || { liability: "", register: "" };
                      const t = tie(v.liability, v.register);
                      return (
                        <tr key={r.key} className="border-t border-border">
                          <td className="py-1.5 font-medium">{r.label}</td>
                          {["liability", "register"].map((f) => (
                            <td key={f} className="py-1.5 text-right">
                              <input type="number" step="0.01" data-testid={`recon-${r.key}-${f}`}
                                value={v[f]} onChange={(e) => setRecon((p) => ({ ...p, [r.key]: { ...p[r.key], [f]: e.target.value } }))}
                                className="h-7 w-28 rounded border border-border bg-background px-1.5 text-right font-mono text-xs" />
                            </td>
                          ))}
                          <td className="py-1.5 text-right text-xs font-bold">
                            {v.liability === "" && v.register === "" ? <span className="text-muted-foreground">—</span>
                              : t ? <span className="text-pos">Ties</span> : <span className="text-neg">Off</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Executive summary */}
              <div className="rounded-lg border border-border bg-card p-4 lg:col-span-2" data-testid="exec-summary">
                <h2 className="mb-3 font-heading text-base font-bold uppercase tracking-wide">Executive Summary</h2>
                <ul className="list-disc space-y-1.5 pl-5 text-sm">
                  {data.exec_summary.map((line, i) => <li key={i}>{line}</li>)}
                </ul>
                <div className="mt-4">
                  <div className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">Additional commentary</div>
                  <Textarea data-testid="commentary-input" value={commentary} onChange={(e) => setCommentary(e.target.value)}
                    placeholder="Add any additional commentary for this payroll…" rows={3} />
                </div>
                <div className="mt-3">
                  <Button data-testid="save-dashboard-btn" onClick={() => save.mutate()} disabled={save.isPending}>
                    {save.isPending ? "Saving…" : "Save reconciliation & commentary"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
